import React from 'react';
import { styled as styled2 } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { colors, fontSizeSmall } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import HelpPanelButton from '@atlassian/jira-help-panel-button';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { main_softwareCodePage_PullRequests$key } from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_PullRequests.graphql';
import {
	featureIdToHelpMap,
	PULL_REQUESTS_ERROR_STATE_TEST_ID,
} from '../../../../common/constants';
import ErrorState from '../../../../common/ui/error-state';
import { useRetryableError } from '../../../../controllers/hooks';
import {
	useViewCodePageExperienceFail,
	VIEW_PR_LIST_EXPERIENCE_NAME,
	ViewCodePageExperienceSuccessTracker,
} from '../../../../services/experience-tracker';
import messages from './messages';
import PullRequestsList from './pull-request-list';

type PullRequestsProps = {
	pullRequestsData: main_softwareCodePage_PullRequests$key;
	onRefetch: () => void;
};
const descriptionFeatureId = 'repository-list.pull-requests.description';

const LearnMoreLink = () => {
	const { helpArticleId, href } = featureIdToHelpMap[descriptionFeatureId] || {};
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const formattedLearnMoreMessage = formatMessage(messages.pullRequestListLearnMore);
	const fallbackAnchor = (
		<Button
			appearance="link"
			spacing="none"
			href={href}
			target="_blank"
			onClick={() =>
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'itemLink',
					}),
					'clickedItemLink',
				)
			}
		>
			{formattedLearnMoreMessage}
		</Button>
	);
	return (
		<HelpPanelButton
			articleId={helpArticleId}
			type="HELP_ARTICLE"
			appearance="link"
			spacing="none"
			onClick={() =>
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'helpLink',
					}),
					'clickedHelpLink',
				)
			}
			fallbackComponent={fallbackAnchor}
		>
			{formattedLearnMoreMessage}
		</HelpPanelButton>
	);
};

export const PullRequests = ({ pullRequestsData, onRefetch }: PullRequestsProps) => {
	const { formatMessage } = useIntl();
	const onPullRequestsExperienceFail = useViewCodePageExperienceFail({
		experienceName: VIEW_PR_LIST_EXPERIENCE_NAME,
	});
	const { onError, onRetry, caughtError } = useRetryableError();

	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_PullRequests on Query
			@argumentDefinitions(
				cloudId: { type: "ID!" }
				projectKey: { type: "String!" }
				updatedFrom: { type: "DateTime!" }
				first: { type: "Int" }
			) {
				...main_softwareCodePage_PullRequestsList
					@arguments(
						cloudId: $cloudId
						projectKey: $projectKey
						updatedFrom: $updatedFrom
						first: $first
					)
			}
		`,
		pullRequestsData,
	);

	const wrappedOnRefetch = () => {
		onRefetch();
		onRetry();
	};

	return (
		<PullRequestsContainer>
			<Heading size="medium" as="h2">
				<TitleContainer data-testid="software-code-page.ui.pages.main-page.pull-requests.title-container">
					{formatMessage(messages.pullRequestListTitle)}
				</TitleContainer>
			</Heading>
			<DescriptionContainer data-testid="software-code-page.ui.pages.main-page.pull-requests.description-container">
				{formatMessage(messages.pullRequestListDescription)} <LearnMoreLink />
			</DescriptionContainer>
			<JSErrorBoundary
				teamName="fusion-isotopes"
				packageName="jiraSoftwareCodePage"
				id="jiraSoftwareCodePagePullRequestsList"
				onError={(location, error, errorAttributes) => {
					onError(error);
					onPullRequestsExperienceFail(location, error, errorAttributes);
				}}
				fallback={() => (
					<>
						<ListWrapper>
							<ErrorState
								testId={PULL_REQUESTS_ERROR_STATE_TEST_ID}
								onRefetch={wrappedOnRefetch}
								description={formatMessage(messages.pullRequestsErrorStateTitle)}
							/>
						</ListWrapper>
					</>
				)}
				resetCaughtError={caughtError}
				sendToPrivacyUnsafeSplunk
			>
				<PullRequestsList pullRequestsListData={data} />
				<ViewCodePageExperienceSuccessTracker experienceName={VIEW_PR_LIST_EXPERIENCE_NAME} />
			</JSErrorBoundary>
		</PullRequestsContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionContainer = styled2.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSizeSmall}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
	margin: `0 0 ${token('space.200', '16px')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PullRequestsContainer = styled2.div({
	margin: `${token('space.400', '32px')} 0px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListWrapper = styled2.div({
	padding: token('space.200', '16px'),
	marginTop: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.sunken', colors.N20),
	borderRadius: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled2.div({
	margin: `0 0 ${token('space.050', '4px')} 0`,
});
